import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddOrganization.css";
import ClientTabs from "./ClientTabs";

const CLIENT_TYPE = "CLIENT";

const emptyForm = {
  name: "",
  legal_name: "",
  code: "",
  country_code: "",
  city: "",
  website: "",
  email: "",
  external_reference: "",
  metadata: "{}",
  is_active: true,
  parent: "",
  industry: "",
};

const AddClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([
    { ...emptyForm },
  ]);

  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });

      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found.",
        });

        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH DATA
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) {
      fetchClients();
      fetchIndustries();
    }
  }, [tenantId]);

  // ----------------------------------------------------------
  // FETCH CLIENTS
  // ONLY organization_type = CLIENT
  // ----------------------------------------------------------

  const fetchClients = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE}/organizations/`
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      const tenantClients = data.filter(
        (client) =>
          Number(client.tenant) === Number(tenantId) &&
          client.organization_type === CLIENT_TYPE
      );

      setClients(tenantClients);
    } catch (error) {
      console.error("Error fetching clients:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to load clients",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Something went wrong while loading clients.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // FETCH INDUSTRIES
  // ----------------------------------------------------------

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/reference-values/`
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      const industryValues = data
        .filter(
          (item) =>
            item.option_set_value?.option_type === "INDUSTRY" &&
            item.is_active
        )
        .map((item) => ({
          id: item.id,
          code: item.code,
          label: item.label,
        }));

      setIndustries(industryValues);
    } catch (error) {
      console.error("Error fetching industries:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to load industries",
        text: "Industry values could not be loaded.",
      });
    }
  };

  // ----------------------------------------------------------
  // HANDLE FORM CHANGE
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) =>
      prev.map((client, clientIndex) =>
        clientIndex === index
          ? {
              ...client,
              [name]:
                type === "checkbox"
                  ? checked
                  : value,
            }
          : client
      )
    );
  };

  // ----------------------------------------------------------
  // ADD CLIENT
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([
      {
        ...emptyForm,
        metadata: "{}",
        is_active: true,
      },
    ]);

    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // ADD ANOTHER CLIENT
  // ----------------------------------------------------------

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyForm,
        metadata: "{}",
        is_active: true,
      },
    ]);
  };

  // ----------------------------------------------------------
  // REMOVE CLIENT FORM
  // ----------------------------------------------------------

  const handleRemoveClientForm = (index) => {
    setFormData((prev) =>
      prev.filter(
        (_, clientIndex) =>
          clientIndex !== index
      )
    );
  };

  // ----------------------------------------------------------
  // EDIT CLIENT
  // ----------------------------------------------------------

  const handleEdit = (client) => {
    setFormData([
      {
        name:
          client.name || "",

        legal_name:
          client.legal_name || "",

        code:
          client.code || "",

        country_code:
          client.country_code || "",

        city:
          client.city || "",

        website:
          client.website || "",

        email:
          client.email || "",

        external_reference:
          client.external_reference || "",

        metadata:
          typeof client.metadata === "object"
            ? JSON.stringify(
                client.metadata,
                null,
                2
              )
            : client.metadata || "{}",

        is_active:
          client.is_active !== undefined
            ? client.is_active
            : true,

        parent:
          client.parent || "",

        industry:
          client.industry || "",
      },
    ]);

    setEditingId(client.id);
    setIsEditing(true);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // DELETE CLIENT
  // ----------------------------------------------------------

  const handleDelete = async (client) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Client?",
      text: `Are you sure you want to delete "${client.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/organizations/${client.id}/`
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Client deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchClients();
    } catch (error) {
      console.error("Error deleting client:", error);

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the client.",
      });
    }
  };

  // ----------------------------------------------------------
  // SUBMIT FORM
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });

      return;
    }

    // --------------------------------------------------------
    // VALIDATE ALL CLIENT FORMS
    // --------------------------------------------------------

    for (let i = 0; i < formData.length; i++) {
      const client = formData[i];

      if (!client.name.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Client Name Required",
          text: `Please enter Client Name for Client ${
            i + 1
          }.`,
        });

        return;
      }

      try {
        if (client.metadata) {
          JSON.parse(client.metadata);
        }
      } catch (error) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Metadata",
          text: `Please enter valid JSON in the metadata field for Client ${
            i + 1
          }.`,
        });

        return;
      }
    }

    try {
      setLoading(true);

      // ------------------------------------------------------
      // EDIT CLIENT
      // ------------------------------------------------------

      if (isEditing) {
        const client = formData[0];

        const parsedMetadata = client.metadata
          ? JSON.parse(client.metadata)
          : {};

        const payload = {
          organization_type: CLIENT_TYPE,

          name:
            client.name,

          legal_name:
            client.legal_name,

          code:
            client.code,

          country_code:
            client.country_code,

          city:
            client.city,

          website:
            client.website || null,

          email:
            client.email || null,

          external_reference:
            client.external_reference,

          metadata:
            parsedMetadata,

          is_active:
            client.is_active,

          tenant:
            Number(tenantId),

          parent:
            client.parent
              ? Number(client.parent)
              : null,

          industry:
            client.industry
              ? Number(client.industry)
              : null,
        };

        await axios.put(
          `${API_BASE}/organizations/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Client Updated",
          text: "Client updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // CREATE MULTIPLE CLIENTS
      // ------------------------------------------------------

      else {
        for (const client of formData) {
          const parsedMetadata =
            client.metadata
              ? JSON.parse(client.metadata)
              : {};

          const payload = {
            // Automatically set to CLIENT
            organization_type: CLIENT_TYPE,

            name:
              client.name,

            legal_name:
              client.legal_name,

            code:
              client.code,

            country_code:
              client.country_code,

            city:
              client.city,

            website:
              client.website || null,

            email:
              client.email || null,

            external_reference:
              client.external_reference,

            metadata:
              parsedMetadata,

            is_active:
              client.is_active,

            tenant:
              Number(tenantId),

            parent:
              client.parent
                ? Number(client.parent)
                : null,

            industry:
              client.industry
                ? Number(client.industry)
                : null,
          };

          await axios.post(
            `${API_BASE}/organizations/`,
            payload
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Clients Created",
          text: `${formData.length} client${
            formData.length > 1
              ? "s"
              : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyForm }]);

      await fetchClients();
    } catch (error) {
      console.error(
        "Error saving clients:",
        error
      );

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save clients.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update failed"
          : "Creation failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(
                backendError
              ),
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // CANCEL
  // ----------------------------------------------------------

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyForm }]);
  };

  // ----------------------------------------------------------
  // INDUSTRY LABEL
  // ----------------------------------------------------------

  const getIndustryLabel = (industryId) => {
    if (!industryId) {
      return "-";
    }

    const industry = industries.find(
      (item) =>
        Number(item.id) ===
        Number(industryId)
    );

    return industry
      ? `${industry.code} - ${industry.label}`
      : `Industry #${industryId}`;
  };

  // ----------------------------------------------------------
  // PARENT CLIENT NAME
  // ----------------------------------------------------------

  const getParentName = (parentId) => {
    if (!parentId) {
      return "-";
    }

    const parent = clients.find(
      (client) =>
        Number(client.id) ===
        Number(parentId)
    );

    return (
      parent?.name ||
      `Client #${parentId}`
    );
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="organization-page">
          <ClientTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>Clients</h2>
                </div>

                <button
                  type="button"
                  className="organization-add-btn"
                  onClick={handleAdd}
                >
                  + Add Client
                </button>
              </div>

              {/* ==================================================
                  CLIENT TABLE
              ================================================== */}

              <div className="organization-list-card">
                {loading ? (
                  <div className="organization-loading">
                    Loading clients...
                  </div>
                ) : clients.length === 0 ? (
                  <div className="organization-empty">
                    <div className="organization-empty-icon">
                      +
                    </div>

                    <h3>
                      No Clients Found
                    </h3>

                    <p>
                      There are no clients
                      available for the
                      current tenant.
                    </p>

                    <button
                      type="button"
                      className="organization-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Client
                    </button>
                  </div>
                ) : (
                  <div className="organization-table-wrapper">
                    <table className="organization-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Code</th>
                          <th>Industry</th>
                          <th>Location</th>
                          <th>Parent Client</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {clients.map(
                          (client) => (
                            <tr
                              key={
                                client.id
                              }
                            >
                              <td>
                                <div className="organization-name-cell">
                                  <div className="organization-avatar">
                                    {client.name
                                      ?.charAt(
                                        0
                                      )
                                      ?.toUpperCase() ||
                                      "C"}
                                  </div>

                                  <div>
                                    <strong>
                                      {client.name ||
                                        "-"}
                                    </strong>

                                    <span>
                                      {client.legal_name ||
                                        "No legal name"}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td>
                                {client.code ||
                                  "-"}
                              </td>

                              <td>
                                {getIndustryLabel(
                                  client.industry
                                )}
                              </td>

                              <td>
                                <div className="organization-location">
                                  <strong>
                                    {client.city ||
                                      "-"}
                                  </strong>

                                  {client.country_code && (
                                    <span>
                                      {
                                        client.country_code
                                      }
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td>
                                {getParentName(
                                  client.parent
                                )}
                              </td>

                              <td>
                                <span
                                  className={`organization-status ${
                                    client.is_active
                                      ? "active"
                                      : "inactive"
                                  }`}
                                >
                                  {client.is_active
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </td>

                              <td>
                                <div className="organization-actions">
                                  <button
                                    type="button"
                                    className="organization-edit-btn"
                                    onClick={() =>
                                      handleEdit(
                                        client
                                      )
                                    }
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    className="organization-delete-btn"
                                    onClick={() =>
                                      handleDelete(
                                        client
                                      )
                                    }
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Client"
                      : "Create Clients"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="organization-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  CLIENT FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map(
                  (
                    client,
                    index
                  ) => (
                    <div
                      className="organization-card"
                      key={index}
                      style={{
                        marginBottom:
                          "20px",
                      }}
                    >
                      <div className="organization-form-body">
                        {/* ==================================================
                            FORM TITLE
                        ================================================== */}

                        <div
                          className="organization-section-title"
                          style={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems:
                              "center",
                          }}
                        >
                          <span>
                            Client{" "}
                            {index + 1}
                          </span>

                          {!isEditing &&
                            formData.length >
                              1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveClientForm(
                                    index
                                  )
                                }
                                style={{
                                  border:
                                    "none",
                                  background:
                                    "transparent",
                                  color:
                                    "#dc2626",
                                  cursor:
                                    "pointer",
                                  fontSize:
                                    "13px",
                                  fontWeight:
                                    "600",
                                }}
                              >
                                Remove
                              </button>
                            )}
                        </div>

                        {/* ==================================================
                            BASIC INFORMATION
                        ================================================== */}

                        <div className="organization-section-title">
                          Client Information
                        </div>

                        <div className="organization-form-grid">
                          {/* CLIENT NAME */}

                          <div className="organization-form-group">
                            <label>
                              Client Name{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <input
                              type="text"
                              name="name"
                              value={
                                client.name
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter client name"
                              maxLength={200}
                              required
                            />
                          </div>

                          {/* LEGAL NAME */}

                          <div className="organization-form-group">
                            <label>
                              Legal Name
                            </label>

                            <input
                              type="text"
                              name="legal_name"
                              value={
                                client.legal_name
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter legal name"
                              maxLength={250}
                            />
                          </div>

                          {/* CLIENT CODE */}

                          <div className="organization-form-group">
                            <label>
                              Client Code
                            </label>

                            <input
                              type="text"
                              name="code"
                              value={
                                client.code
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter client code"
                              maxLength={60}
                            />
                          </div>

                          {/* INDUSTRY */}

                          <div className="organization-form-group">
                            <label>
                              Industry
                            </label>

                            <select
                              name="industry"
                              value={
                                client.industry
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            >
                              <option value="">
                                Select
                                industry
                              </option>

                              {industries.map(
                                (
                                  industry
                                ) => (
                                  <option
                                    key={
                                      industry.id
                                    }
                                    value={
                                      industry.id
                                    }
                                  >
                                    {
                                      industry.code
                                    }{" "}
                                    -{" "}
                                    {
                                      industry.label
                                    }
                                  </option>
                                )
                              )}
                            </select>
                          </div>

                          {/* COUNTRY CODE */}

                          <div className="organization-form-group">
                            <label>
                              Country
                              Code
                            </label>

                            <input
                              type="text"
                              name="country_code"
                              value={
                                client.country_code
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="e.g. IN"
                              maxLength={2}
                            />
                          </div>

                          {/* CITY */}

                          <div className="organization-form-group">
                            <label>
                              City
                            </label>

                            <input
                              type="text"
                              name="city"
                              value={
                                client.city
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter city"
                              maxLength={120}
                            />
                          </div>

                          {/* WEBSITE */}

                          <div className="organization-form-group">
                            <label>
                              Website
                            </label>

                            <input
                              type="url"
                              name="website"
                              value={
                                client.website
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="https://example.com"
                            />
                          </div>

                          {/* EMAIL */}

                          <div className="organization-form-group">
                            <label>
                              Email
                            </label>

                            <input
                              type="email"
                              name="email"
                              value={
                                client.email
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter client email"
                            />
                          </div>

                          {/* EXTERNAL REFERENCE */}

                          <div className="organization-form-group">
                            <label>
                              External
                              Reference
                            </label>

                            <input
                              type="text"
                              name="external_reference"
                              value={
                                client.external_reference
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter external reference"
                              maxLength={100}
                            />
                          </div>

                          {/* PARENT CLIENT */}

                          <div className="organization-form-group">
                            <label>
                              Parent Client
                            </label>

                            <select
                              name="parent"
                              value={
                                client.parent
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            >
                              <option value="">
                                No parent
                                client
                              </option>

                              {clients
                                .filter(
                                  (
                                    existingClient
                                  ) =>
                                    Number(
                                      existingClient.id
                                    ) !==
                                    Number(
                                      editingId
                                    )
                                )
                                .map(
                                  (
                                    existingClient
                                  ) => (
                                    <option
                                      key={
                                        existingClient.id
                                      }
                                      value={
                                        existingClient.id
                                      }
                                    >
                                      {
                                        existingClient.name
                                      }
                                    </option>
                                  )
                                )}
                            </select>
                          </div>

                          {/* STATUS */}

                          <div className="organization-status-field">
                            <label>
                              Client
                              Status
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_active"
                                checked={
                                  client.is_active
                                }
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {client.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* ==================================================
                    ADD ANOTHER CLIENT
                ================================================== */}

                {!isEditing && (
                  <button
                    type="button"
                    className="organization-add-btn"
                    onClick={
                      handleAddAnother
                    }
                    style={{
                      marginBottom:
                        "20px",
                    }}
                  >
                    + Add Another Client
                  </button>
                )}

                {/* ==================================================
                    FORM ACTIONS
                ================================================== */}

                <div className="organization-form-actions">
                  <button
                    type="button"
                    className="organization-cancel-btn"
                    onClick={
                      handleCancel
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="organization-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Client"
                      : "Save Clients"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddClients;